'use client';

import { useEffect, useState, useCallback, useRef } from 'react';

export interface SessionTimeoutConfig {
  totalTtlSeconds: number;
  warningThresholdSeconds: number;
  expiresAt?: number;
  onTimeout: () => void;
  onExtend: () => void;
}

export interface SessionTimeoutState {
  secondsUntilWarning: number;
  secondsUntilTimeout: number;
  isWarning: boolean;
  extend: () => void;
  logout: () => void;
}

export function useSessionTimeout(config: SessionTimeoutConfig): SessionTimeoutState {
  const [secondsUntilTimeout, setSecondsUntilTimeout] = useState(config.totalTtlSeconds);
  const onTimeoutRef = useRef(config.onTimeout);
  const onExtendRef = useRef(config.onExtend);
  const timeoutFiredRef = useRef(false);
  const [initialDeadline] = useState(() => config.expiresAt ?? Date.now() + config.totalTtlSeconds * 1000);
  const deadlineRef = useRef(initialDeadline);

  useEffect(() => {
    onTimeoutRef.current = config.onTimeout;
    onExtendRef.current = config.onExtend;
  }, [config.onTimeout, config.onExtend]);

  useEffect(() => {
    if (config.expiresAt !== undefined) {
      deadlineRef.current = config.expiresAt;
    }
  }, [config.expiresAt]);

  const extend = useCallback(() => {
    timeoutFiredRef.current = false;
    deadlineRef.current = config.expiresAt ?? Date.now() + config.totalTtlSeconds * 1000;
    setSecondsUntilTimeout(Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)));
    onExtendRef.current();
  }, [config.totalTtlSeconds, config.expiresAt]);

  const logout = useCallback(() => {
    if (!timeoutFiredRef.current) {
      timeoutFiredRef.current = true;
      onTimeoutRef.current();
    }
  }, []);

  useEffect(() => {
    if (secondsUntilTimeout <= 0) {
      if (!timeoutFiredRef.current) {
        timeoutFiredRef.current = true;
        onTimeoutRef.current();
      }
      return;
    }

  }, [secondsUntilTimeout]);

  const isExpired = secondsUntilTimeout <= 0;
  const isWarning = !isExpired && secondsUntilTimeout <= config.warningThresholdSeconds;

  useEffect(() => {
    if (isExpired) {
      return;
    }

    const interval = setInterval(() => {
      setSecondsUntilTimeout(Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)));
    }, 1000);

    return () => clearInterval(interval);
  }, [isExpired]);

  const secondsUntilWarning = Math.max(0, secondsUntilTimeout - config.warningThresholdSeconds);

  return {
    secondsUntilWarning,
    secondsUntilTimeout,
    isWarning,
    extend,
    logout,
  };
}
